import React, { useEffect, useState } from 'react'
import { Title, useDataProvider, useNotify } from 'react-admin'
import {
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { Alert, AlertTitle } from '@material-ui/lab'
import { useHistory } from 'react-router-dom'
import ReactJson from 'react-json-view'
import moment from 'moment'
import { retryIntegration, closeIntegration } from '../../../providers/dataProvider'

const useStyles = makeStyles((theme) => ({
  padding2: {
    padding: theme.spacing(2)
  },
  mt2: {
    marginTop: '2em'
  },
  mb2: {
    marginBottom: '2em'
  }
}))

const getPreInvoiceId = (props) => {
  if (
    props &&
    props.match &&
    props.match.params &&
    props.match.params.id &&
    props.match.params.id !== 'undefined'
  ) {
    return props.match.params.id
  }

  const hash = window.location.hash || ''
  const match = hash.match(/\/zicsa\/preinvoices\/([^/]+)\/show/)

  if (match && match[1] && match[1] !== 'undefined') {
    return match[1]
  }

  return null
}

const statusLabel = (status) => {
  switch (status) {
    case 'Finished':
      return 'Integrado'
    case 'Failed':
      return 'Fallido'
    case 'Pendient':
      return 'Pendiente'
    default:
      return status || '--'
  }
}

const statusColor = (status) => {
  switch (status) {
    case 'Finished':
      return '#008000'
    case 'Failed':
      return '#ff0000'
    case 'Pendient':
      return '#f5a623'
    default:
      return '#9e9e9e'
  }
}

const formatDate = (value) => {
  if (!value) {
    return '--'
  }

  const date = moment(value)

  if (!date.isValid()) {
    return value
  }

  return date.format('DD-MM-YYYY')
}

const parseJsonOrNull = (value) => {
  const text = String(value || '').trim()

  const candidates = [
    text,
    text.replace(/\\"/g, '"'),
    text
      .replace(/'/g, '"')
      .replace(/\bNULL\b/g, 'null')
      .replace(/\bNone\b/g, 'null')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false'),
    text
      .replace(/\\"/g, '"')
      .replace(/'/g, '"')
      .replace(/\bNULL\b/g, 'null')
      .replace(/\bNone\b/g, 'null')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')
  ]

  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate)
    } catch (e) {
      // Continua intentando con el siguiente formato
    }
  }

  return null
}

const normalizeJson = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (Array.isArray(value)) {
    return value.map(item => normalizeJson(item))
  }

  if (typeof value === 'object') {
    return Object.keys(value).reduce((result, key) => {
      result[key] = normalizeJson(value[key])
      return result
    }, {})
  }

  if (typeof value === 'string') {
    let currentValue = value.trim()

    if (currentValue === '') {
      return null
    }

    for (let index = 0; index < 8; index += 1) {
      const parsedValue = parseJsonOrNull(currentValue)

      if (parsedValue === null) {
        return currentValue
      }

      if (typeof parsedValue === 'string') {
        const nextValue = parsedValue.trim()

        if (nextValue === currentValue) {
          return nextValue
        }

        currentValue = nextValue
      }
      else {
        return normalizeJson(parsedValue)
      }
    }

    return currentValue
  }

  return value
}

const isObjectValue = (value) => {
  return value !== null && typeof value === 'object'
}

const renderPrimitive = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--'
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }

  return String(value)
}

const JsonViewer = ({ value }) => {
  const normalized = normalizeJson(value)

  if (normalized === null || normalized === undefined || normalized === '') {
    return (
      <div style={{ padding: '16px', background: '#fafafa' }}>
        Sin informacion
      </div>
    )
  }

  if (!isObjectValue(normalized)) {
    return (
      <div style={{ padding: '16px', background: '#fafafa' }}>
        {renderPrimitive(normalized)}
      </div>
    )
  }

  return (
    <ReactJson
      src={normalized}
      name={false}
      collapsed={false}
      displayDataTypes={false}
      enableClipboard={true}
      style={{
        fontSize: '13px',
        background: 'transparent'
      }}
    />
  )
}

const Section = ({ title, children }) => {
  const classes = useStyles()

  return (
    <React.Fragment>
      <Typography
        style={{ marginBottom: '1em' }}
        variant="h6"
        component="h6"
      >
        {title}
      </Typography>

      {children}

      <Divider
        className={`${classes.mb2} ${classes.mt2}`}
      />
    </React.Fragment>
  )
}

const getErrorInfo = (error) => {
  const normalized = normalizeJson(error)

  if (!normalized) {
    return null
  }

  if (typeof normalized === 'string') {
    return {
      origin: 'Registro Pre-Factura',
      message: normalized
    }
  }

  return {
    origin:
      normalized.origin ||
      normalized.Origin ||
      normalized.place ||
      normalized.Place ||
      normalized.step ||
      normalized.Step ||
      normalized.operation ||
      normalized.Operation ||
      'Registro Pre-Factura',
    message:
      normalized.message ||
      normalized.Message ||
      normalized.error ||
      normalized.Error ||
      normalized.MessageText ||
      JSON.stringify(normalized)
  }
}

const ActionsButton = ({ record = {}, onRefresh }) => {
  const notify = useNotify()
  const [open, setOpen] = useState(false)

  if (record.status !== 'Failed') {
    return null
  }

  const resourceItemId = record.resourceitemid || record.id

  if (!resourceItemId) {
    return null
  }

  const retry = () => {
    retryIntegration('docuclass', 'preinvoice', resourceItemId)
      .then(success => {
        if (success) {
          notify(
            'La pre-factura se ha vuelto a procesar. Espera unos segundos a que termine y recarga para ver los resultados.',
            'info',
            null,
            false,
            3000
          )
        }
        else {
          notify(
            'No ha sido posible volver a procesar la pre-factura. Intentalo nuevamente.',
            'warning',
            null,
            false,
            3000
          )
        }
      })
  }

  const close = () => {
    setOpen(false)

    closeIntegration('docuclass', 'preinvoice', resourceItemId)
      .then(success => {
        if (success) {
          notify(
            'La pre-factura ha sido cerrada.',
            'info',
            null,
            false,
            3000
          )

          if (typeof onRefresh === 'function') {
            onRefresh()
          }
        }
        else {
          notify(
            'No ha sido posible cerrar la pre-factura. Intentalo nuevamente.',
            'warning',
            null,
            false,
            3000
          )
        }
      })
  }

  return (
    <React.Fragment>
      <ButtonGroup
        color="primary"
        size="small"
        style={{ marginTop: '8px' }}
      >
        <Button onClick={() => setOpen(true)}>
          Cerrar
        </Button>

        <Button onClick={retry}>
          Reproceso
        </Button>
      </ButtonGroup>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
      >
        <DialogTitle>Cerrar pre-factura</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Deseas cerrar esta pre-factura? No podras revertir el cambio.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            color="primary"
          >
            NO
          </Button>

          <Button
            onClick={close}
            color="primary"
          >
            SI
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}
const IntegrationResultShow = props => {
  const classes = useStyles()
  const history = useHistory()
  const dataProvider = useDataProvider()
  const id = getPreInvoiceId(props)

  const [record, setRecord] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  const refreshRecord = () => {
    if (!id) {
      return
    }

    dataProvider
      .getOne('zicsa/preinvoices', { id })
      .then(response => {
        setRecord(response.data)
        setLoadError(null)
      })
      .catch(error => {
        setLoadError(error.message || 'No se pudo actualizar el detalle.')
      })
  }

  useEffect(() => {
    if (!id) {
      setLoading(false)
      setLoadError('No se recibio ID de pre-factura.')
      return
    }

    setLoading(true)
    setLoadError(null)

    dataProvider
      .getOne('zicsa/preinvoices', { id })
      .then(response => {
        setRecord(response.data)
        setLoading(false)
      })
      .catch(error => {
        setLoadError(error.message || 'No se pudo cargar el detalle.')
        setLoading(false)
      })
  }, [dataProvider, id])

  if (loading) {
    return <LinearProgress />
  }

  if (loadError) {
    return (
      <Container>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="flex-start"
          spacing={2}
        >
          <Grid item sm={9} xs={12}>
            <Card className={classes.padding2}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={1}>
                  <IconButton onClick={() => history.push('/zicsa/preinvoices')}>
                    <ArrowBackIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={11}>
                  <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    {loadError}
                  </Alert>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Container>
    )
  }

  if (!record) {
    return (
      <Container>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="flex-start"
          spacing={2}
        >
          <Grid item sm={9} xs={12}>
            <Card className={classes.padding2}>
              <Typography>No se encontro la pre-factura.</Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>
    )
  }

  const errorInfo = getErrorInfo(record.error)
  const bodyValue = record.body || (record.status === 'Failed' ? 'Error antes de generar body' : null)

  return (
    <Container>
      <Title
        title={`Pre-factura, ${
          record.numero_factura ||
          record.numero_orden_compra ||
          record.id
        }`}
      />

      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        spacing={2}
      >
        <Grid item sm={9} xs={12}>
          <Card className={classes.padding2}>
            <CardContent>
              <Grid
                container
                direction="row"
                justifyContent="space-around"
                alignItems="center"
              >
                <div>
                  <IconButton onClick={() => history.push('/zicsa/preinvoices')}>
                    <ArrowBackIcon />
                  </IconButton>
                </div>

                <div>
                  <Typography variant="body1" component="p">
                    <b>Estado:</b>{' '}
                    <span
                      style={{
                        backgroundColor: statusColor(record.status),
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '4px 10px',
                        fontWeight: 700,
                        fontSize: '13px'
                      }}
                    >
                      {statusLabel(record.status)}
                    </span>
                  </Typography>

                  <Typography variant="body1" component="p">
                    <b>Fecha inicio integración:</b>{' '}
                    {formatDate(record.integration_createdat || record.createdat)}
                  </Typography>

                  <Typography variant="body1" component="p">
                    <b>Fecha integración completada:</b>{' '}
                    {formatDate(record.integration_updatedat || record.updatedat)}
                  </Typography>

                  <ActionsButton
                    record={record}
                    onRefresh={refreshRecord}
                  />
                </div>
              </Grid>

              <Divider
                className={`${classes.mb2} ${classes.mt2}`}
              />

              {
                errorInfo && (
                  <React.Fragment>
                    <Alert
                      severity="error"
                      icon={<ErrorOutlineIcon />}
                      style={{ marginTop: '1em' }}
                    >
                      <AlertTitle>Error</AlertTitle>
                      <AlertTitle>Ocurrio en: {errorInfo.origin}</AlertTitle>
                      {errorInfo.message}
                    </Alert>

                    <Divider
                      className={`${classes.mb2} ${classes.mt2}`}
                    />
                  </React.Fragment>
                )
              }

              <Section title="Datos pre-factura Docuclass">
                <TableContainer style={{ background: '#fafafa' }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell style={{ fontWeight: 700, width: '260px' }}>
                          ID Pre-factura
                        </TableCell>
                        <TableCell>{record.id || '--'}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell style={{ fontWeight: 700 }}>
                          Numero Factura
                        </TableCell>
                        <TableCell>{record.numero_factura || '--'}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell style={{ fontWeight: 700 }}>
                          Numero OC
                        </TableCell>
                        <TableCell>{record.numero_orden_compra || '--'}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell style={{ fontWeight: 700 }}>
                          Fecha Factura
                        </TableCell>
                        <TableCell>{record.fecha_factura || '--'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Section>

              <Section title="JSON recibido por controlador">
                <JsonViewer value={record.data} />
              </Section>

              <Section title="JSON Body enviado a SAP">
                <JsonViewer value={bodyValue} />
              </Section>

              <Section title="Respuesta cruda de SAP">
                <JsonViewer value={record.response} />
              </Section>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

export default IntegrationResultShow